import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getMentorCourseDropoutApi } from "../../features/analytics/api/mentorAnalytics.api"
import { CourseDropout } from "../../features/analytics/api/adminAnalytics.api"
import { ChevronLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/16/solid"

const color = (rate: number) =>
  rate >= 75 ? "bg-emerald-500" : rate >= 50 ? "bg-amber-400" : "bg-red-400"

const textColor = (rate: number) =>
  rate >= 75 ? "text-emerald-600" : rate >= 50 ? "text-amber-600" : "text-red-500"

export const MentorAnalyticsCourseDropoutPage = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const [data, setData] = useState<CourseDropout | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    if (!courseId) return
    setLoading(true)
    setError(false)
    getMentorCourseDropoutApi(courseId)
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [courseId]) // eslint-disable-line

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link
          to={`/mentor/analytics/courses/${courseId}`}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Назад к курсу
        </Link>
        <span className="text-gray-300">/</span>
        <h2 className="text-base font-semibold text-gray-800">Dropout анализ</h2>
      </div>

      {error ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400 mb-2">Не удалось загрузить dropout-анализ</p>
          <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
            Повторить
          </button>
        </div>
      ) : loading || !data ? (
        <div className="space-y-4">
          {[1, 2].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-32 animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="flex gap-4 text-xs font-medium">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> ≥75% — норма</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /> 50–74% — риск</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400 inline-block" /> &lt;50% — критично</span>
          </div>

          {data.modules.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center text-gray-300 text-sm">
              Нет данных по этому курсу
            </div>
          ) : (
            data.modules.map((mod) => (
              <div key={mod.module_id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                <div className="px-5 py-3.5 bg-gray-50/60 border-b">
                  <h3 className="font-semibold text-gray-700 text-sm">{mod.title}</h3>
                </div>
                <div className="divide-y divide-gray-50">
                  {mod.lessons.map((lesson) => (
                    <div key={lesson.lesson_id} className="px-5 py-4 hover:bg-gray-50/40 transition-colors">
                      <div className="flex items-center justify-between mb-2.5">
                        <span className="text-sm font-medium text-gray-800">{lesson.title}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-gray-400">{lesson.total_viewers} просмотров</span>
                          <span className={`text-xs font-semibold ${textColor(lesson.completion_rate)}`}>
                            {lesson.completion_rate}% завершили
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${color(lesson.completion_rate)}`}
                            style={{ width: `${lesson.completion_rate}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 w-28 text-right">
                          Отсев: {lesson.avg_dropout_percent}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  )
}
